if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.activeMediaGroup = '';

        this.ensureMobileDots();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              mediaToSwitch
                .querySelector('button')
                .addEventListener(
                  'click',
                  this.setActiveMedia.bind(
                    this,
                    mediaToSwitch.dataset.target,
                    false
                  )
                );
            });
        }

        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        window.requestAnimationFrame(() => {
          const activeMedia =
            this.elements.viewer?.querySelector(
              '[data-media-id].is-active'
            ) ||
            this.elements.viewer?.querySelector(
              '[data-media-id]'
            );

          this.applyMediaGroup(
            activeMedia?.dataset.mediaGroup || ''
          );
        });
      }

      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;

        if (!currentMedia) return;

        const thumbnail =
          this.elements.thumbnails?.querySelector(
            `[data-target="${currentMedia.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
        this.updateVisibleCounter(
          currentMedia.dataset.mediaId
        );
        this.updateMobileDots(
          currentMedia.dataset.mediaId
        );
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          );

        if (!activeMedia) return;

        this.applyMediaGroup(
          activeMedia.dataset.mediaGroup || ''
        );

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);
        this.updateMobileDots(mediaId);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);
        this.updateVisibleCounter(mediaId);

        this.announceLiveRegion(
          activeMedia,
          this.getVisibleMediaPosition(mediaId)
        );
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        thumbnail
          .querySelector('button')
          .setAttribute('aria-current', true);

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      applyMediaGroup(group) {
        if (!this.elements.viewer) return;

        let normalizedGroup =
          this.normalizeMediaGroup(group);

        /*
         * Keep shared images inside the currently selected
         * variant group instead of revealing every image.
         */
        if (
          normalizedGroup === 'shared' &&
          this.activeMediaGroup
        ) {
          normalizedGroup =
            this.activeMediaGroup;
        }

        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            '[data-media-id]'
          )
        );

        const hasMatchingGroup =
          mediaItems.some(
            (item) =>
              this.normalizeMediaGroup(
                item.dataset.mediaGroup
              ) === normalizedGroup
          );

        const shouldFilter =
          normalizedGroup &&
          normalizedGroup !== 'shared' &&
          hasMatchingGroup;

        this.activeMediaGroup =
          shouldFilter
            ? normalizedGroup
            : '';

        const visibleMediaIds = new Set();

        mediaItems.forEach((item) => {
          const itemGroup =
            this.normalizeMediaGroup(
              item.dataset.mediaGroup
            );

          const isVisible =
            !shouldFilter ||
            itemGroup === normalizedGroup ||
            itemGroup === 'shared';

          this.setMediaVisibility(
            item,
            isVisible
          );

          if (isVisible) {
            visibleMediaIds.add(
              item.dataset.mediaId
            );
          }
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((thumbnail) => {
              this.setMediaVisibility(
                thumbnail,
                visibleMediaIds.has(
                  thumbnail.dataset.target
                )
              );
            });
        }

        this.filterModalMedia(
          visibleMediaIds
        );

        this.refreshSliders();
      }

      normalizeMediaGroup(group) {
        return String(group || '')
          .trim()
          .toLowerCase();
      }

      setMediaVisibility(
        element,
        isVisible
      ) {
        if (!element) return;

        element.hidden = !isVisible;

        element.classList.toggle(
          'stitches-variant-media-hidden',
          !isVisible
        );

        element.setAttribute(
          'aria-hidden',
          isVisible ? 'false' : 'true'
        );
      }

      filterModalMedia(
        visibleMediaIds
      ) {
        const sectionId = this.id
          .replace(/^MediaGallery-/, '')
          .replace(/-duplicate$/, '');

        const modal =
          document.getElementById(
            `ProductModal-${sectionId}`
          );

        const modalContent =
          modal?.querySelector(
            '.product-media-modal__content'
          );

        if (!modalContent) return;

        const visibleNumericIds =
          new Set(
            Array.from(
              visibleMediaIds,
              (mediaId) =>
                mediaId.split('-').pop()
            )
          );

        Array.from(
          modalContent.children
        ).forEach((media) => {
          if (!media.dataset.mediaId) return;

          this.setMediaVisibility(
            media,
            visibleNumericIds.has(
              media.dataset.mediaId
            )
          );
        });
      }

      refreshSliders() {
        window.requestAnimationFrame(() => {
          if (
            typeof this.elements.viewer
              ?.resetPages === 'function'
          ) {
            this.elements.viewer.resetPages();
          }

          if (
            typeof this.elements.thumbnails
              ?.resetPages === 'function'
          ) {
            this.elements.thumbnails.resetPages();
          }

          const activeMedia =
            this.elements.viewer?.querySelector(
              '[data-media-id].is-active:not([hidden])'
            ) ||
            this.elements.viewer?.querySelector(
              '[data-media-id]:not([hidden])'
            );

          if (activeMedia) {
            this.updateVisibleCounter(
              activeMedia.dataset.mediaId
            );

            this.renderMobileDots(
              activeMedia.dataset.mediaId
            );
          }
        });
      }

      getVisibleMediaItems() {
        return Array.from(
          this.elements.viewer?.querySelectorAll(
            '[data-media-id]:not([hidden])'
          ) || []
        );
      }

      getVisibleMediaPosition(
        mediaId
      ) {
        const position =
          this.getVisibleMediaItems()
            .findIndex(
              (media) =>
                media.dataset.mediaId ===
                mediaId
            ) + 1;

        return Math.max(position, 1);
      }

      updateVisibleCounter(mediaId) {
        const current =
          this.elements.viewer?.querySelector(
            '.slider-counter--current'
          );

        const total =
          this.elements.viewer?.querySelector(
            '.slider-counter--total'
          );

        if (current) {
          current.textContent =
            this.getVisibleMediaPosition(
              mediaId
            );
        }

        if (total) {
          total.textContent =
            this.getVisibleMediaItems().length;
        }
      }

      ensureMobileDots() {
        if (!this.elements.viewer) {
          return null;
        }

        const counter =
          this.elements.viewer.querySelector(
            '.slider-counter'
          );

        if (!counter) return null;

        let dots =
          counter.querySelector(
            '.stitches-mobile-gallery-dots'
          );

        if (!dots) {
          dots =
            document.createElement('span');

          dots.className =
            'stitches-mobile-gallery-dots';

          dots.setAttribute(
            'aria-label',
            'Product gallery'
          );

          counter.appendChild(dots);
        }

        this.mobileDots = dots;

        return dots;
      }

      renderMobileDots(
        activeMediaId
      ) {
        const dots =
          this.ensureMobileDots();

        if (!dots) return;

        const visibleItems =
          this.getVisibleMediaItems();

        dots.innerHTML = '';

        visibleItems.forEach(
          (media, index) => {
            const dot =
              document.createElement(
                'button'
              );

            dot.type = 'button';

            dot.className =
              'stitches-mobile-gallery-dot';

            dot.setAttribute(
              'aria-label',
              `View product image ${
                index + 1
              }`
            );

            const isActive =
              media.dataset.mediaId ===
              activeMediaId;

            dot.classList.toggle(
              'is-active',
              isActive
            );

            if (isActive) {
              dot.setAttribute(
                'aria-current',
                'true'
              );
            }

            dot.addEventListener(
              'click',
              () => {
                const slider =
                  this.elements.viewer
                    .slider ||
                  media.parentElement;

                slider.scrollTo({
                  left: media.offsetLeft,
                  behavior: 'smooth',
                });
              }
            );

            dots.appendChild(dot);
          }
        );
      }

      updateMobileDots(
        activeMediaId
      ) {
        const dots =
          this.ensureMobileDots();

        if (!dots) return;

        const visibleItems =
          this.getVisibleMediaItems();

        const currentDots =
          Array.from(
            dots.querySelectorAll(
              '.stitches-mobile-gallery-dot'
            )
          );

        if (
          currentDots.length !==
          visibleItems.length
        ) {
          this.renderMobileDots(
            activeMediaId
          );

          return;
        }

        currentDots.forEach(
          (dot, index) => {
            const isActive =
              visibleItems[index]
                ?.dataset.mediaId ===
              activeMediaId;

            dot.classList.toggle(
              'is-active',
              isActive
            );

            if (isActive) {
              dot.setAttribute(
                'aria-current',
                'true'
              );
            } else {
              dot.removeAttribute(
                'aria-current'
              );
            }
          }
        );
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion
            .setAttribute(
              'aria-hidden',
              false
            );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings
              .imageAvailable.replace(
                '[index]',
                position
              );

          setTimeout(() => {
            this.elements.liveRegion
              .setAttribute(
                'aria-hidden',
                true
              );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) {
          return;
        }

        this.elements.viewer.slider
          .setAttribute(
            'role',
            'presentation'
          );

        this.elements.viewer.sliderItems
          .forEach((slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          });
      }
    }
  );
}